import React, { ReactNode } from 'react';
import Pagination from '@/components/ui/pagination';
import Loading from '@/components/ui/loading';
import { cn } from '@/lib/utils';

export type DataTableServerColumn<T> = {
  key: string;
  title: ReactNode;
  width?: string;
  className?: string;
  render?: (row: T, index: number) => ReactNode;
};

type DataTableServerProps<T> = {
  columns: DataTableServerColumn<T>[];
  data: T[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  loading?: boolean;
  rowKey?: (row: T, index: number) => string;
  onPaginationChange: (page: number, pageSize: number) => void;
  emptyText?: ReactNode;
  className?: string;
};

export function DataTableServer<T>({
  columns,
  data,
  totalCount,
  pageIndex,
  pageSize,
  loading,
  rowKey,
  onPaginationChange,
  emptyText,
  className,
}: DataTableServerProps<T>) {
  return (
    <div className={cn('w-full', className)}>
      <div className="min-h-[320px] overflow-hidden rounded-lg border border-solid border-[#f0f0f0] bg-white">
        <Loading
          loading={loading}
          minHeight="320px"
          maxWidth="220px"
          className="w-full"
        >
          <div className="w-full overflow-x-auto">
            <table className="w-max min-w-[1180px] table-fixed border-separate border-spacing-0 text-sm">
              <thead className="bg-[#fafafa]">
                <tr>
                  {columns.map(column => (
                    <th
                      key={column.key}
                      style={{ width: column.width }}
                      className={cn(
                        'whitespace-nowrap border-0 border-b border-solid border-[#f0f0f0] bg-[#fff1e3] px-5 py-4 text-left font-bold text-[#f06f24]',
                        column.className,
                      )}
                    >
                      {column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length ? (
                  data.map((row, index) => (
                    <tr
                      key={
                        rowKey?.(row, index) ??
                        `${pageIndex}-${index}-${JSON.stringify(row)}`
                      }
                      className="bg-white hover:bg-[#fffaf2]"
                    >
                      {columns.map(column => (
                        <td
                          key={column.key}
                          className={cn(
                            'whitespace-nowrap border-0 border-b border-solid border-[#f0f0f0] px-5 py-3.5 text-left align-middle text-[#111111]',
                            column.className,
                          )}
                        >
                          {column.render
                            ? column.render(row, index)
                            : (row as any)[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="h-24 px-3 py-6 text-center text-[#6f8295]"
                    >
                      {emptyText}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Loading>
      </div>

      <Pagination
        className="w-full bg-[#f6f6f8] px-8 py-4 pt-4"
        current={pageIndex + 1}
        pageSize={pageSize}
        total={totalCount}
        showSizeChanger
        onChange={onPaginationChange}
      />
    </div>
  );
}
